import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

const CustomH1 = ({ children }) => (
  <h1 className="text-3xl font-bold mb-4">{children}</h1>
);

const CustomH2 = ({ children }) => (
  <h2 className="text-2xl font-bold">{children}</h2>
);

const CustomH3 = ({ children }) => (
  <h3 className="text-xl font-bold">{children}</h3>
);

const CustomH4 = ({ children }) => (
  <h4 className="text-lg font-bold">{children}</h4>
);

const CustomH5 = ({ children }) => (
  <h5 className="text-base font-bold">{children}</h5>
);

const CustomH6 = ({ children }) => (
  <h6 className="text-sm font-bold">{children}</h6>
);

const CustomParagraph = ({ children }) => (
  <p className="text-sm md:text-base leading-relaxed mb-2">{children}</p>
);

const CustomList = ({ children }) => (
  <ul className="list-disc list-outside leading-relaxed mb-2 pl-6 space-y-1">
    {children}
  </ul>
);

const CustomOrderedList = ({ children }) => (
  <ol className="list-decimal list-outside leading-relaxed mb-2 pl-6 space-y-1">
    {children}
  </ol>
);

const CustomListItem = ({ children }) => (
  <li className="leading-relaxed mb-1 pl-1">{children}</li>
);

const CustomCode = ({ children }) => (
  <code className="px-1.5 py-0.5 rounded text-sm font-mono">
    {children}
  </code>
);

const CustomPre = ({ children }) => (
  <pre className="bg-gray-800 bg-opacity-80 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4 text-sm font-mono">
    {children}
  </pre>
);

const CustomBlockquote = ({ children }) => (
  <blockquote className="border-l-4 border-[#4940A9] pl-4 italic my-4 text-gray-700">
    {children}
  </blockquote>
);

const CustomStrong = ({ children }) => (
  <strong className="font-semibold text-[#3429B3]">{children}</strong>
);

const CustomEm = ({ children }) => (
  <em className="italic">{children}</em>
);

const CustomA = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#4940A9] hover:text-[#AE2DC7] underline">
    {children}
  </a>
);

const Typewriter = ({ text, delay, onUpdate, onComplete }) => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCalledComplete = useRef(false);

  useEffect(() => {
    // Reset when text changes
    setCurrentText('');
    setCurrentIndex(0);
    hasCalledComplete.current = false;
  }, [text]);

  useEffect(() => {
    if (currentIndex < text?.length) {
      const timeout = setTimeout(() => {
        setCurrentText(prevText => prevText + text[currentIndex]);
        setCurrentIndex(prevIndex => prevIndex + 1);
        // Trigger scroll update callback
        if (onUpdate) {
          onUpdate();
        }
      }, delay);
  
      return () => clearTimeout(timeout);
    } else if (currentIndex >= text?.length && text?.length > 0 && !hasCalledComplete.current) {
      // Typewriter is complete
      hasCalledComplete.current = true;
      if (onComplete) {
        onComplete();
      }
    }
  }, [currentIndex, delay, text, onUpdate, onComplete]);

  return (
    <ReactMarkdown 
      components={{
        h1: CustomH1,
        h2: CustomH2,
        h3: CustomH3,
        h4: CustomH4,
        h5: CustomH5,
        h6: CustomH6,
        p: CustomParagraph,
        ul: CustomList,
        ol: CustomOrderedList,
        li: CustomListItem,
        code: CustomCode,
        pre: CustomPre,
        blockquote: CustomBlockquote,
        strong: CustomStrong,
        em: CustomEm,
        a: CustomA,
      }}
    >
      {currentText}
    </ReactMarkdown>
  );
};

export default Typewriter;